// Standalone duplicate of assets/lead-capture-modal.js (renamed class, storage
// keys, global registry, and data attributes), redesigned around a fixed
// single product (no more product picker) and a two-field (email/phone) form —
// do not merge changes back into that file.
const QUOTE_MODAL_DRAFT_KEY = 'quote-modal-form-draft';

class QuoteModal {
  constructor(modal) {
    this.modalId = modal.id;
    this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.lastTrigger = null;
    this.isSelectedInEditor = false;
    this.submissionPrepared = false;

    this.captureRefs(modal);

    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);

    document.addEventListener('click', this.boundHandleClick);
    document.addEventListener('keydown', this.boundHandleKeydown);

    if (this.isDesignMode) {
      this.bindEditorPreviewEvents();
    }

    this.checkAutoOpen();
  }

  // (Re)reads every DOM reference this instance depends on and rewires the
  // listeners scoped to those elements. Called on construction, and again
  // whenever the theme editor swaps in freshly re-rendered section HTML
  // (see the shopify:section:load handler below) — a settings change
  // replaces the whole section subtree, which would otherwise leave this
  // instance holding references to now-detached nodes.
  captureRefs(modal) {
    this.modal = modal;
    this.dialog = modal.querySelector('.quote-modal__dialog');
    this.sectionId = modal.dataset.sectionId || '';

    this.formState = modal.querySelector('[data-quote-modal-form-state]');
    this.successState = modal.querySelector('[data-quote-modal-success-state]');

    this.form = modal.querySelector('form');
    this.formId = this.form ? this.form.id : null;
    this.submitButton = modal.querySelector('[data-submit-btn]');

    // Server-rendered from the section's "Product" setting as a fallback, but
    // normally overwritten per-click by applyProductData() from whichever
    // trigger opened the modal (see snippets/quote-modal-trigger.liquid).
    this.productSummary = modal.querySelector('[data-product-summary]');
    this.productThumb = modal.querySelector('[data-product-thumb]');
    this.productTitleEl = modal.querySelector('[data-product-title]');
    this.productDescEl = modal.querySelector('[data-product-desc]');
    this.productPriceEl = modal.querySelector('[data-product-price]');
    this.productIdField = modal.querySelector('[data-product-id-field]');
    this.productField = modal.querySelector('[data-product-field]');
    this.productUrlField = modal.querySelector('[data-product-url-field]');

    this.emailField = modal.querySelector('[data-field-role="email"]');
    this.phoneField = modal.querySelector('[data-field-role="phone"]');

    // Bound to both the button's click and the form's submit: Shopify layers invisible
    // hCaptcha verification on top of this form, which intercepts the first 'submit' event
    // and later completes the real submission via form.submit() — a call that does not fire
    // a 'submit' event at all. Click always fires first and can't be skipped that way, so the
    // pre-submission work (Klaviyo, draft save, redirect tagging) runs there; the submit
    // listener is a fallback for implicit (Enter-key) submissions, guarded so it never runs
    // twice for the same submission.
    if (this.submitButton) {
      this.submitButton.addEventListener('click', this.prepareSubmission.bind(this));
    }
    if (this.form) {
      this.form.addEventListener('submit', this.prepareSubmission.bind(this));
    }

    this.bindSubmitState();
    this.bindFieldValidation();
  }

  // Format-checks the email/phone fields ourselves rather than trusting the browser's
  // native constraint validation for them: type="email" validation varies in strictness
  // across browsers, and pattern-attribute enforcement on type="tel" inputs is
  // inconsistent (notably unreliable in Safari), so relying on field.checkValidity()
  // alone let malformed values through silently in some browsers. An empty value isn't
  // flagged here — phone is optional, and email's emptiness is the "required" field's job.
  isFieldFormatValid(field) {
    if (!field) return true;
    const value = field.value.trim();
    if (value === '') return true;

    if (field === this.emailField) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
    if (field === this.phoneField) {
      const digits = value.replace(/\D/g, '');
      return digits.length === 10 || (digits.length === 11 && digits.charAt(0) === '1');
    }
    return true;
  }

  // Enables the submit button once the form's native validation passes (email
  // required) and both email/phone pass our own format check.
  // Single source of truth for "is this form submittable right now" — shared
  // by bindSubmitState (button appearance) and prepareSubmission (the actual
  // submit-time guard), so the two can never drift out of sync.
  isFormValid() {
    if (!this.form) return false;
    return this.form.checkValidity()
      && this.isFieldFormatValid(this.emailField)
      && this.isFieldFormatValid(this.phoneField);
  }

  // Uses aria-disabled rather than the disabled attribute, deliberately: a
  // disabled button is pulled out of the tab order entirely, and Dawn's
  // trapFocus() (global.js) snapshots the focusable-elements list once, at
  // modal-open time — so a *disabled-at-open* submit button would remain
  // permanently unreachable by keyboard even after becoming valid. Leaving it
  // focusable (just visually + functionally inert via the button--invalid
  // class's `pointer-events: none` and the isFormValid() guard in
  // prepareSubmission) keeps it in the trap boundary from the start.
  bindSubmitState() {
    if (!this.submitButton || !this.form) return;

    const validLabel = this.submitButton.dataset.validLabel || 'Get Custom Pricing';
    const invalidLabel = this.submitButton.dataset.invalidLabel || 'Please fill out required fields';

    const update = () => {
      if (this.isFormValid()) {
        this.submitButton.classList.remove('button--invalid');
        this.submitButton.setAttribute('aria-disabled', 'false');
        this.submitButton.textContent = validLabel;
      } else {
        this.submitButton.classList.add('button--invalid');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.textContent = invalidLabel;
      }
    };

    this.form.addEventListener('input', update);
    this.form.addEventListener('change', update);
    update();
  }

  // Shows a red outline + inline "Invalid entry" message on the email/phone fields once
  // the shopper has left the field (blur) with a value that fails our own format check —
  // never before they've had a chance to type anything, and never for a merely-empty
  // field (that's handled by bindSubmitState/native validation on submit instead).
  bindFieldValidation() {
    [this.emailField, this.phoneField].forEach((field) => {
      if (!field) return;
      const wrapper = field.closest('[data-field-wrapper]');
      const errorEl = wrapper ? wrapper.querySelector('[data-field-error]') : null;
      if (!wrapper || !errorEl) return;

      const evaluate = () => {
        const touched = wrapper.dataset.touched === 'true';
        const invalid = touched && field.value.trim() !== '' && !this.isFieldFormatValid(field);
        wrapper.classList.toggle('quote-modal__field--invalid', invalid);
        errorEl.hidden = !invalid;
      };

      field.addEventListener('blur', () => {
        wrapper.dataset.touched = 'true';
        evaluate();
      });
      field.addEventListener('input', evaluate);
    });
  }

  // Lets a merchant preview the modal just by clicking its section in the theme
  // editor sidebar, instead of having to click a trigger on the live page —
  // same convention as sections/contact-success-modal.liquid.
  bindEditorPreviewEvents() {
    document.addEventListener('shopify:section:select', (event) => {
      if (event.detail && event.detail.sectionId === this.sectionId) {
        this.isSelectedInEditor = true;
        this.showFormState();
        this.open();
      }
    });

    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.detail && event.detail.sectionId === this.sectionId) {
        this.isSelectedInEditor = false;
        this.close();
      }
    });

    // Every settings edit makes the theme editor re-render this section's HTML
    // from scratch and swap it into the page, which silently closes the modal
    // (the fresh markup starts `hidden` again) and leaves this instance
    // pointing at now-detached nodes. Re-point it at the new DOM and, if the
    // section is still selected for editing, reopen it so the preview doesn't
    // blink shut on every keystroke.
    document.addEventListener('shopify:section:load', (event) => {
      if (!event.detail || event.detail.sectionId !== this.sectionId) return;

      const freshModal = document.getElementById(this.modalId);
      if (!freshModal) return;

      this.captureRefs(freshModal);
      window.quoteModals[freshModal.id] = this;

      if (this.isSelectedInEditor) {
        this.showFormState();
        this.open();
      }
    });
  }

  trackKlaviyo() {
    if (typeof window.klaviyo === 'undefined' || typeof window.klaviyo.identify !== 'function') return;

    const productTitle = this.productField ? this.productField.value : '';

    window.klaviyo.identify({
      $email: this.emailField ? this.emailField.value.trim() : '',
      $phone_number: this.phoneField ? this.phoneField.value.trim() : '',
    });

    window.klaviyo.track('Submitted Product Interest Form', {
      'Product': productTitle,
    });
  }

  // Generic over whatever fields are present, so nothing typed is lost on error.
  saveDraft() {
    if (!this.form) return;

    const draft = {};
    Array.from(this.form.elements).forEach((field) => {
      if (!field.name) return;
      draft[field.name] = field.value;
    });

    try {
      window.sessionStorage.setItem(QUOTE_MODAL_DRAFT_KEY, JSON.stringify(draft));
    } catch (error) {
      // sessionStorage unavailable — a server error just won't be able to restore what they typed.
    }
  }

  restoreDraft() {
    if (!this.form) return;

    let raw = null;
    try {
      raw = window.sessionStorage.getItem(QUOTE_MODAL_DRAFT_KEY);
    } catch (error) {
      raw = null;
    }
    if (!raw) return;

    try {
      const draft = JSON.parse(raw);
      Array.from(this.form.elements).forEach((field) => {
        if (!field.name || !(field.name in draft)) return;
        field.value = draft[field.name];
        // Setting .value directly doesn't fire input/change, so without this
        // bindSubmitState's listeners never re-run — the button would stay
        // stuck on "Please fill out required fields" even once the (valid)
        // restored values are back in the fields.
        field.dispatchEvent(new Event('input', { bubbles: true }));
      });
    } catch (error) {
      // malformed draft — nothing to restore.
    }

    this.clearDraft();
  }

  clearDraft() {
    try {
      window.sessionStorage.removeItem(QUOTE_MODAL_DRAFT_KEY);
    } catch (error) {
      // no-op
    }
  }

  showSuccessState() {
    this.modal.classList.add('quote-modal--success');
  }

  showFormState() {
    this.modal.classList.remove('quote-modal--success');
  }

  prepareSubmission(event) {
    // The submit button is only *visually* + aria-disabled while invalid (see
    // bindSubmitState), not natively disabled, so nothing else stops an
    // invalid submission from reaching here — a keyboard user activating the
    // button with Enter/Space, or pressing Enter inside a field, both still
    // fire this. Block it explicitly instead of relying on the browser.
    if (!this.isFormValid()) {
      if (event) event.preventDefault();
      return;
    }

    if (this.submissionPrepared) return;
    this.submissionPrepared = true;

    this.trackKlaviyo();
    this.saveDraft();

    if (this.formId) {
      try {
        window.sessionStorage.setItem('shopify-form-submitted', this.formId);
      } catch (error) {
        // sessionStorage unavailable (e.g. private browsing) — auto-open/restore just won't fire.
      }
    }
  }

  checkAutoOpen() {
    if (!this.formId) return;

    let submittedFormId = null;
    try {
      submittedFormId = window.sessionStorage.getItem('shopify-form-submitted');
    } catch (error) {
      submittedFormId = null;
    }

    if (!submittedFormId || submittedFormId !== this.formId) return;

    try {
      window.sessionStorage.removeItem('shopify-form-submitted');
    } catch (error) {
      // no-op
    }

    const marker = document.querySelector('meta[name="shopify-contact-status"]');
    const status = marker ? marker.getAttribute('content') : null;

    if (status === 'success') {
      this.handleSuccessfulSubmission();
    } else if (status === 'error') {
      this.handleFailedSubmission();
    }
  }

  // What happens once we know the previous submission went through. Swaps
  // the form for an in-modal success state and leaves the modal open (does
  // NOT auto-close it) so the shopper actually sees the confirmation.
  handleSuccessfulSubmission() {
    this.clearDraft();

    // TODO: replace success state with redirect to /pages/what-to-expect
    this.showSuccessState();
    this.open();
  }

  // What happens once we know the previous submission failed server-side
  // validation. Reopens on the form (not the success state), with the
  // inline error banner from sections/quote-modal.liquid's `form.errors`
  // check already rendered server-side, and restores whatever the shopper
  // had typed so they don't have to redo it.
  handleFailedSubmission() {
    this.showFormState();
    this.restoreDraft();
    this.open();
  }

  // Overwrites the modal's product summary (image, title, description, price)
  // and the hidden Product ID/Title/URL fields from a trigger's
  // data-quote-product-* attributes (see snippets/quote-modal-trigger.liquid).
  // A trigger without those attributes (e.g. a generic "Get in Touch" link)
  // leaves the section-configured fallback product in place untouched.
  applyProductData(trigger) {
    if (!trigger) return;
    const data = trigger.dataset;
    if (!data.quoteProductId && !data.quoteProductTitle) return;

    if (this.productSummary) this.productSummary.hidden = false;

    if (this.productThumb) {
      this.productThumb.innerHTML = '';
      if (data.quoteProductImage) {
        const img = document.createElement('img');
        img.src = data.quoteProductImage;
        img.alt = data.quoteProductTitle || '';
        img.loading = 'lazy';
        this.productThumb.appendChild(img);
      }
    }

    if (this.productTitleEl) this.productTitleEl.textContent = data.quoteProductTitle || '';

    if (this.productDescEl) {
      const hasDesc = Boolean(data.quoteProductDescription);
      this.productDescEl.textContent = data.quoteProductDescription || '';
      this.productDescEl.hidden = !hasDesc;
    }

    if (this.productPriceEl) this.productPriceEl.textContent = data.quoteProductPrice || '';

    if (this.productIdField) this.productIdField.value = data.quoteProductId || '';
    if (this.productField) this.productField.value = data.quoteProductTitle || '';
    if (this.productUrlField) this.productUrlField.value = data.quoteProductUrl || '';
  }

  handleClick(event) {
    // Two ways in: data-quote-modal-open for code-rendered triggers (the
    // quote-modal-trigger snippet), and a magic URL fragment so a merchant can point
    // *any* theme-editor "Button link" field (image-with-text, header CTA, multicolumn, rich
    // text, etc.) at the modal without touching code.
    const trigger = event.target.closest(`[data-quote-modal-open="${this.modalId}"]`);
    const linkTrigger = event.target.closest('a[href*="#quote-modal"]');
    if (trigger || linkTrigger) {
      event.preventDefault();
      this.lastTrigger = trigger || linkTrigger;
      this.applyProductData(this.lastTrigger);
      this.open();
      return;
    }

    if (!this.isOpen()) return;

    if (event.target.closest('[data-quote-modal-close]') && this.modal.contains(event.target.closest('[data-quote-modal-close]'))) {
      event.preventDefault();
      this.close();
    }
  }

  handleKeydown(event) {
    if (event.code && event.code.toUpperCase() === 'ESCAPE' && this.isOpen()) {
      this.close();
    }
  }

  isOpen() {
    return !this.modal.hasAttribute('hidden');
  }

  open() {
    if (this.isOpen()) return;

    this.modal.removeAttribute('hidden');
    document.body.classList.add('overflow-hidden');

    const activeState = this.modal.classList.contains('quote-modal--success') ? this.successState : this.formState;
    const searchRoot = activeState || this.modal;
    const firstFocusable = searchRoot.querySelector('input, select, textarea, button, a[href]');
    if (typeof trapFocus === 'function') {
      trapFocus(this.dialog, firstFocusable || this.dialog);
    } else if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  close() {
    this.modal.setAttribute('hidden', 'hidden');
    document.body.classList.remove('overflow-hidden');
    this.showFormState();

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.lastTrigger || null);
    }
    this.lastTrigger = null;
  }
}

if (!window.quoteModals) {
  window.quoteModals = {};
  document.querySelectorAll('[data-quote-modal]').forEach((modal) => {
    window.quoteModals[modal.id] = new QuoteModal(modal);
  });
}
